import { CALL_API, CHAIN_API } from '../../../middleware/api';
import { convertMoneyToNumber } from '../../../utils/string';
import { dateToString } from '../../../utils/dateFormat';

export const LOADING_RETAINER = Symbol('LOADING_RETAINER');
export const LOADED_COMPANY_RETAINER = Symbol('LOADED_COMPANY_RETAINER');
export const UPDATE_COMPANY = Symbol('UPDATE_COMPANY');
export const REVERT_UPDATE = Symbol('REVERT_UPDATE');
export const LOADED_USERS = Symbol('LOADED_USERS');
export const SAVED_COMPANY_BUYER = Symbol('SAVED_COMPANY_BUYER');
export const ERRORED_COMPANY_BUYER = Symbol('ERRORED_COMPANY_BUYER');
export const CLOSE_COMPANY_BUYER_VALIDATION_ERROR = Symbol('CLOSE_COMPANY_BUYER_VALIDATION_ERROR');
export const ADD_COMPANY_RETAINER = Symbol('ADD_COMPANY_RETAINER');
export const DELETE_COMPANY_RETAINER = Symbol('DELETE_COMPANY_RETAINER');
export const FIND_TARGETS = Symbol('FIND_TARGETS');
export const UPDATE_AGREEMENT_CATEGORIES = Symbol('UPDATE_AGREEMENT_CATEGORIES');
export const UPDATE_COMPANY_RETAINER_AGREEMENT_CATEGORIES = Symbol('UPDATE_COMPANY_RETAINER_AGREEMENT_CATEGORIES');
export const LOADED_COMPANY_BUYERS_LIST = Symbol('LOADED_COMPANY_BUYERS_LIST');
export const FETCHING_AGREEMENT_BUYERS = Symbol('FETCHING_AGREEMENT_BUYERS');
export const FETCHING_AGREEMENT_BUYERS_FAILURE = Symbol('FETCHING_AGREEMENT_BUYERS_FAILURE');
export const FETCHING_AGREEMENT_BUYERS_SUCCESS = Symbol('FETCHING_AGREEMENT_BUYERS_SUCCESS');
export const FETCHING_RETAINER_FILES = Symbol('FETCHING_RETAINER_FILES');
export const FETCHING_RETAINER_FILES_FAILURE = Symbol('FETCHING_RETAINER_FILES_FAILURE');
export const FETCHING_RETAINER_FILES_SUCCESS = Symbol('FETCHING_RETAINER_FILES_SUCCESS');
export const DELETE_COMPANY_RETAINER_FILE_SUCCESS = Symbol('DELETE_COMPANY_RETAINER_FILE_SUCCESS');

export function handleUpdateCompany(info) {
  return {
    type: UPDATE_COMPANY,
    ...info,
  };
}

export function getRetainer({ companyId }) {
  return {
    [CALL_API]: {
      method: 'get',
      path: `/api/v1/companies/${companyId}?include=agreement_categories`,
      startType: LOADING_RETAINER,
      successType: LOADED_COMPANY_RETAINER,
    },
  };
}

export function saveInfo({ companyId, body }) {
  return {
    fields: Object.keys(body),
    [CALL_API]: {
      method: 'put',
      unifier: `buyer put /api/v1/companies/${companyId} {${JSON.stringify(body)}}`,
      path: `/api/v1/companies/${companyId}`,
      body: {
        ...body,
        retainer: body.retainer ? convertMoneyToNumber(body.retainer) : body.retainer,
        start_date: body.startDate === '' ? null : dateToString(body.startDate),
        end_date: body.endDate === '' ? null : dateToString(body.endDate),
        notification_cancellation_date:
          body.notificationCancellationDate === '' ? null : dateToString(body.notificationCancellationDate),
        notification_start_date: body.notificationStartDate === '' ? null : dateToString(body.notificationStartDate),
      },
      successType: SAVED_COMPANY_BUYER,
      errorType: ERRORED_COMPANY_BUYER,
      skipGlobalErrorHandler: true,
    },
  };
}

export function updateCompanyRetainers({ companyId, body, afterSuccess }) {
  return {
    [CALL_API]: {
      method: 'post',
      path: `/api/v1/companies/${companyId}/bulk/buyer_retainers`,
      body: { data: body },
      afterSuccess,
    },
  };
}

export function closeValidationError({ field }) {
  return {
    type: CLOSE_COMPANY_BUYER_VALIDATION_ERROR,
    field,
  };
}

export function revertUpdate({ field }) {
  return {
    type: REVERT_UPDATE,
    field,
  };
}

export function updateRetainer({ companyId, value }) {
  return function(dispatch) {
    dispatch({
      [CALL_API]: {
        method: 'put',
        unifier: `buyers retainer put /api/v1/companies/${companyId}`,
        path: `/api/v1/companies/${companyId}`,
        body: {
          start_date: value && value.isValid() ? value.format('YYYY-MM-DD') : null,
        },
        afterError: ({ dispatch }) => {
          dispatch(getRetainer({ companyId }));
        },
        skipGlobalErrorHandler: true,
      },
    });
  };
}

export function addCompanyRetainer(retainer) {
  return {
    type: ADD_COMPANY_RETAINER,
    retainer,
  };
}

export function removeCompanyRetainer(id, deleteType) {
  return {
    type: DELETE_COMPANY_RETAINER,
    id,
    deleteType,
  };
}

/**
 * Get targets list by search value.
 *
 * @param {string} filter Search value.
 * @param {number} page Number page.
 * @param {Function} afterSuccess After success callback function.
 */
export function findTargets({ filter, page, afterSuccess }) {
  return {
    filter,
    page,
    [CALL_API]: {
      method: 'get',
      path: '/api/v1/companies',
      query: {
        like: filter,
        type: 'target',
        limit: 100,
        page,
      },
      successType: FIND_TARGETS,
      afterSuccess,
    },
  };
}

export function updateAgreementCategories(agreementCategories) {
  return {
    type: UPDATE_AGREEMENT_CATEGORIES,
    agreementCategories,
  };
}

export function updateCompanyRetainerAgreementCategories(agreementCategories, index) {
  return {
    type: UPDATE_COMPANY_RETAINER_AGREEMENT_CATEGORIES,
    agreementCategories,
    index,
  };
}

export function findBuyers({ afterSuccess, filter, ...rest }) {
  return {
    ...rest,
    [CALL_API]: {
      method: 'get',
      path: '/api/v1/browse/buyer_names',
      unifier: params => `${params.path}`,
      maxCount: 2,
      query: {
        like: filter,
      },
      successType: LOADED_COMPANY_BUYERS_LIST,
      afterSuccess,
    },
  };
}

export function getCompanyRetainersByCompanyId({ companyId, afterSuccess, afterError }) {
  return {
    [CALL_API]: {
      method: 'get',
      path: `/api/v1/companies/${companyId}`,
      query: {
        include: ['company_retainers'].join(','),
      },
      startType: FETCHING_AGREEMENT_BUYERS,
      errorType: FETCHING_AGREEMENT_BUYERS_FAILURE,
      successType: FETCHING_AGREEMENT_BUYERS_SUCCESS,
      afterSuccess,
      afterError,
    },
  };
}

export function uploadAgreementFile({ companyId, formData }) {
  return {
    [CHAIN_API]: [
      () => ({
        [CALL_API]: {
          method: 'post',
          path: `/api/v1/companies/${companyId}/agreements/files`,
          body: formData,
        },
      }),
      () => ({
        [CALL_API]: {
          method: 'get',
          path: `/api/v1/companies/${companyId}/agreements/files`,
          successType: FETCHING_RETAINER_FILES_SUCCESS,
        },
      }),
    ],
  };
}

export function getCompanyRetainerFiles({ companyId, afterSuccess, afterError }) {
  return {
    [CALL_API]: {
      method: 'get',
      path: `/api/v1/companies/${companyId}/agreements/files`,
      startType: FETCHING_RETAINER_FILES,
      errorType: FETCHING_RETAINER_FILES_FAILURE,
      successType: FETCHING_RETAINER_FILES_SUCCESS,
      afterSuccess,
      afterError,
    },
  };
}

export function deleteCompanyRetainerFile({ companyId, id }, afterSuccess) {
  return {
    [CHAIN_API]: [
      () => ({
        companyId,
        id,
        [CALL_API]: {
          method: 'delete',
          path: `/api/v1/companies/${companyId}/agreements/files/${id}`,
          successType: DELETE_COMPANY_RETAINER_FILE_SUCCESS,
          afterSuccess,
        },
      }),
    ],
  };
}
